/**
 * Authentication synchronization utility for cross-tab authentication
 * This ensures that when a user logs in/out in one tab, other tabs are notified
 */
 
// Custom event for logout to ensure cross-tab synchronization
const LOGOUT_EVENT = 'app-logout';
 
// Periodic authentication check interval (5 minutes)
const AUTH_CHECK_INTERVAL = 5 * 60 * 1000;
 
// Dedicated broadcast key for cross-tab logout
const LOGOUT_BROADCAST_KEY = 'auth:logout';
 
// Listen for storage changes in other tabs
export const setupAuthSync = () => {
  const handleStorageChange = (event) => {
    // React to explicit auth keys, clear events, or logout broadcast
    const isAuthKey = event.key === 'jwtToken' || event.key === 'login_response_auth' || event.key === 'expirationTime';
    const isClearEvent = event.key === null; // localStorage.clear() from another tab
    const isLogoutBroadcast = event.key === LOGOUT_BROADCAST_KEY;
 
    if (isAuthKey || isClearEvent || isLogoutBroadcast) {
      console.log('Cross-tab auth: Storage change detected:', event.key);
 
      // Check if user is still authenticated
      const token = localStorage.getItem('jwtToken') || sessionStorage.getItem('jwtToken');
      const loginResponse = localStorage.getItem('login_response_auth') || sessionStorage.getItem('login_response_auth');
 
      if (isLogoutBroadcast || !token || !loginResponse) {
        console.log('Cross-tab auth: User not authenticated, redirecting to login');
        if (window.location.pathname !== '/authentication/sign-in') {
          window.location.href = '/authentication/sign-in';
        }
      }
    }
  };
 
  // Handle custom logout events from other tabs
  const handleLogoutEvent = () => {
    console.log('Cross-tab auth: Logout event received from another tab');
    // Clear local storage and redirect to login
      const keysToRemove = [
      "login_response",
      "login_response_auth",
      "jwtToken",
      "refreshToken",
      "expirationTime",
      "EmpName",
      "currentDesignation",
      "photoBase64", 
      "username",   
      "calendarEvents",
      "conferenceBookings",
      "calendarEventData",
      "conferenceBookingsData",
      "Employee_Details"
    ];

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    sessionStorage.clear();
    if (window.location.pathname !== '/authentication/sign-in') {
      window.location.href = '/authentication/sign-in';
    }
  };
 
  // Handle tab focus events to check authentication
  const handleTabFocus = () => {
    // Check authentication when tab becomes active
    if (!isAuthenticated()) {
      console.log('Cross-tab auth: Tab focus - user not authenticated, logging out');
      logoutFromAllTabs();
    }
  };
 
  // Periodic authentication check
  const periodicAuthCheck = () => {
    if (!isAuthenticated()) {
      console.log('Cross-tab auth: Periodic check - user not authenticated, logging out');
      logoutFromAllTabs();
    }
  };
 
  // Set up periodic check
  const authCheckInterval = setInterval(periodicAuthCheck, AUTH_CHECK_INTERVAL);
 
  // Add event listeners
  window.addEventListener('storage', handleStorageChange);
  window.addEventListener(LOGOUT_EVENT, handleLogoutEvent);
  window.addEventListener('focus', handleTabFocus);
  window.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      handleTabFocus();
    }
  });
 
  console.log('Cross-tab auth: Authentication sync setup complete');
 
  // Return cleanup function
  return () => {
    window.removeEventListener('storage', handleStorageChange);
    window.removeEventListener(LOGOUT_EVENT, handleLogoutEvent);
    window.removeEventListener('focus', handleTabFocus);
    window.removeEventListener('visibilitychange', handleTabFocus);
    clearInterval(authCheckInterval);
    console.log('Cross-tab auth: Authentication sync cleanup complete');
  };
};
 
// Function to logout from all tabs
export const logoutFromAllTabs = () => {
  console.log('Cross-tab auth: Logging out from all tabs');
 
  // Broadcast a logout signal so other tabs receive a concrete storage event
  try {
    localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()));
  } catch (e) {
    // ignore
  }
 
  const keysToRemove = [
    "login_response",
    "login_response_auth",
    "jwtToken",
    "refreshToken",
    "expirationTime",
    "EmpName",
    "currentDesignation",
    "photoBase64", 
    "username",   
    "calendarEvents",
    "conferenceBookings",
    "calendarEventData",
    "conferenceBookingsData",
    "Employee_Details"
  ];

  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });
  sessionStorage.clear();
 
  // Dispatch custom event to notify other listeners within this tab
  window.dispatchEvent(new CustomEvent(LOGOUT_EVENT));
 
  // Redirect current tab
  if (window.location.pathname !== '/authentication/sign-in') {
    window.location.href = '/authentication/sign-in';
  }
};
 
// Function to check if user is authenticated across tabs
export const isAuthenticated = () => {
  const token = localStorage.getItem('jwtToken') || sessionStorage.getItem('jwtToken');
  const loginResponse = localStorage.getItem('login_response_auth') || sessionStorage.getItem('login_response_auth');
  const expirationTime = localStorage.getItem('expirationTime') || sessionStorage.getItem('expirationTime');
 
  return token && loginResponse && (!expirationTime || Date.now() < parseInt(expirationTime));
};
 
// Function to get authentication data from localStorage first, then sessionStorage
export const getAuthData = (key) => {
  return localStorage.getItem(key) || sessionStorage.getItem(key);
};
 
// Function to set authentication data in both storages for backward compatibility
export const setAuthData = (key, value) => {
  localStorage.setItem(key, value);
  sessionStorage.setItem(key, value);
};
 
// Helper function to replace window.sessionStorage.getItem calls throughout the app
// This ensures cross-tab compatibility while maintaining backward compatibility
export const getSessionData = (key) => {
  // For backward compatibility, still check sessionStorage first if it exists
  // but fall back to localStorage for cross-tab persistence
  const sessionValue = sessionStorage.getItem(key);
  const localValue = localStorage.getItem(key);
 
  // If both exist, prefer the most recent one
  if (sessionValue && localValue) {
    // For critical auth data, prefer localStorage for cross-tab consistency
    if (['jwtToken', 'refreshToken', 'login_response_auth', 'expirationTime'].includes(key)) {
      return localValue;
    }
    // For other data, prefer sessionStorage for backward compatibility
    return sessionValue;
  }
 
  return sessionValue || localValue;
};
 
// Helper function to replace window.sessionStorage.setItem calls
export const setSessionData = (key, value) => {
  // Store in both storages for cross-tab compatibility
  localStorage.setItem(key, value);
  sessionStorage.setItem(key, value);
};
 
 